import React, { useState } from 'react';
import {
  Check,
  Camera,
  Cloud,
  HardDrive,
  Scissors,
  Youtube,
  Mail,
  Archive,
  CheckCircle2,
  Circle,
  ExternalLink,
} from 'lucide-react';
import { MarketingContent, MarketingContentPatch } from '../../types/marketing';
import { WorkflowTemplate, StageFieldKey } from '../../utils/marketingTemplates';
import './StageChecklist.css';

interface StageChecklistProps {
  content: MarketingContent;
  template: WorkflowTemplate;
  onToggle: (stageId: string, done: boolean) => void;
  onStageNote: (stageId: string, note: string) => void;
  onUpdateField: (patch: MarketingContentPatch) => void;
}

/** Lucide icons are ForwardRefExoticComponents; React.ElementType accepts them. */
function iconForStage(name: string, size = 14): React.ReactNode {
  const map: Record<string, React.ElementType> = {
    Camera, Cloud, HardDrive, Scissors, Youtube, Mail, Archive,
  };
  const Cmp = map[name];
  return Cmp ? <Cmp size={size} /> : <Circle size={size} />;
}

const StageChecklist: React.FC<StageChecklistProps> = ({
  content,
  template,
  onToggle,
  onStageNote,
  onUpdateField,
}) => {
  return (
    <ol className="mh-stages">
      {template.stages.map((stage, idx) => {
        const status = content.stageStatuses?.[stage.id] ?? { status: 'todo' as const };
        const isDone = status.status === 'done';
        const isActive = content.currentStageId === stage.id && !isDone;
        return (
          <li
            key={stage.id}
            className={`mh-stage ${isDone ? 'done' : ''} ${isActive ? 'active' : ''}`}
          >
            <div className="mh-stage-marker-col">
              <button
                type="button"
                className={`mh-stage-checkbox ${isDone ? 'checked' : ''}`}
                onClick={() => onToggle(stage.id, !isDone)}
                aria-pressed={isDone}
                aria-label={isDone ? `Mark "${stage.label}" not done` : `Mark "${stage.label}" done`}
                style={isDone ? { background: stage.color, borderColor: stage.color } : { borderColor: stage.color }}
              >
                {isDone ? <Check size={14} strokeWidth={3} /> : null}
              </button>
              {idx < template.stages.length - 1 && <div className="mh-stage-connector" />}
            </div>

            <div className="mh-stage-body">
              <div className="mh-stage-head">
                <div className="mh-stage-head-left">
                  <span className="mh-stage-icon" style={{ color: stage.color }}>
                    {iconForStage(stage.icon, 14)}
                  </span>
                  <span className="mh-stage-label">{stage.label}</span>
                  {isActive && <span className="mh-stage-current">Current</span>}
                  {isDone && status.completedAt?.toDate && (
                    <span className="mh-stage-done-at">
                      {status.completedAt.toDate().toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                      })}
                    </span>
                  )}
                </div>
              </div>

              {stage.description && (
                <p className="mh-stage-desc">{stage.description}</p>
              )}

              {stage.fields && stage.fields.length > 0 && (
                <div className="mh-stage-fields">
                  {stage.fields.map(field => (
                    <StageField
                      key={field}
                      field={field}
                      content={content}
                      onUpdateField={onUpdateField}
                    />
                  ))}
                </div>
              )}

              {stage.actionLabel && !isDone && (
                <button
                  type="button"
                  className="mh-stage-action"
                  onClick={() => onToggle(stage.id, true)}
                >
                  <CheckCircle2 size={13} />
                  {stage.actionLabel}
                </button>
              )}

              <StageNote
                stageId={stage.id}
                value={status.note ?? ''}
                onSave={v => onStageNote(stage.id, v)}
              />
            </div>
          </li>
        );
      })}
    </ol>
  );
};

/* ── Per-stage inline field editor ───────────────────────── */

interface StageFieldProps {
  field: StageFieldKey;
  content: MarketingContent;
  onUpdateField: (patch: MarketingContentPatch) => void;
}

const FIELD_LABELS: Record<StageFieldKey, string> = {
  recordingDate: 'Recording date',
  publishDate: 'Publish date',
  guestName: 'Guest',
  dropboxRawUrl: 'Dropbox raw URL',
  dropboxArchiveUrl: 'Dropbox archive URL',
  ssdPath: 'SSD path',
  youtubeUrl: 'YouTube URL',
  thumbnailUrl: 'Thumbnail URL',
  notes: 'Edit notes',
};

const FIELD_PLACEHOLDERS: Record<StageFieldKey, string> = {
  recordingDate: '',
  publishDate: '',
  guestName: 'e.g. Jane Smith',
  dropboxRawUrl: 'https://www.dropbox.com/...',
  dropboxArchiveUrl: 'https://www.dropbox.com/...',
  ssdPath: '/Volumes/EditSSD/Episode-23/',
  youtubeUrl: 'https://youtu.be/...',
  thumbnailUrl: 'https://...',
  notes: 'Cuts, B-roll callouts, fixes…',
};

const StageField: React.FC<StageFieldProps> = ({ field, content, onUpdateField }) => {
  const isDate = field === 'recordingDate' || field === 'publishDate';
  const isLongText = field === 'notes';
  const isUrl = field === 'dropboxRawUrl' || field === 'dropboxArchiveUrl' || field === 'youtubeUrl' || field === 'thumbnailUrl';

  const dateVal =
    isDate && content[field]
      ? (content[field] as { toDate?: () => Date } | null)?.toDate?.().toISOString().slice(0, 10) ?? ''
      : '';

  const stringVal = !isDate ? ((content[field] as string | null) ?? '') : '';

  return (
    <div className="mh-stage-field">
      <label className="mh-stage-field-label">{FIELD_LABELS[field]}</label>
      {isDate ? (
        <input
          type="date"
          className="mh-stage-input"
          value={dateVal}
          onChange={e => {
            const next = e.target.value ? new Date(e.target.value + 'T00:00:00') : null;
            onUpdateField({ [field]: next ?? null } as MarketingContentPatch);
          }}
        />
      ) : isLongText ? (
        <textarea
          className="mh-stage-input mh-stage-textarea"
          placeholder={FIELD_PLACEHOLDERS[field]}
          value={stringVal}
          rows={3}
          onChange={e => onUpdateField({ [field]: e.target.value || null } as MarketingContentPatch)}
        />
      ) : (
        <div className="mh-stage-field-row">
          <input
            type={isUrl ? 'url' : 'text'}
            className="mh-stage-input"
            placeholder={FIELD_PLACEHOLDERS[field]}
            value={stringVal}
            onChange={e => onUpdateField({ [field]: e.target.value || null } as MarketingContentPatch)}
          />
          {isUrl && stringVal && (
            <a
              href={stringVal}
              target="_blank"
              rel="noopener noreferrer"
              className="mh-stage-field-open"
              aria-label="Open link"
              title="Open link"
            >
              <ExternalLink size={12} />
            </a>
          )}
        </div>
      )}
    </div>
  );
};

/* ── Per-stage note (debounced save on blur) ─────────────── */

interface StageNoteProps {
  stageId: string;
  value: string;
  onSave: (v: string) => void;
}

const StageNote: React.FC<StageNoteProps> = ({ stageId, value, onSave }) => {
  const [local, setLocal] = useState(value);
  const [open, setOpen] = useState(Boolean(value));

  React.useEffect(() => {
    setLocal(value);
  }, [value, stageId]);

  if (!open && !local) {
    return (
      <button
        type="button"
        className="mh-stage-note-toggle"
        onClick={() => setOpen(true)}
      >
        + Add note
      </button>
    );
  }

  return (
    <textarea
      className="mh-stage-note"
      placeholder="Notes for this stage…"
      value={local}
      rows={2}
      onChange={e => setLocal(e.target.value)}
      onBlur={() => {
        if (local !== value) onSave(local);
        if (!local) setOpen(false);
      }}
    />
  );
};

export default StageChecklist;
